import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { user, userType } = useAuth();
  const [onboardingState, setOnboardingState] = useState({
    hasSeenWelcome: false,
    hasSeenDashboardTips: false,
    hasSeenEventsTips: false,
    hasSeenSettingsTips: false,
    hasSeenMarketplaceTips: false,
    hasSeenReceiptsTips: false,
    hasSeenCRMTips: false,
    hasSeenAnalyticsTips: false,
    completedSteps: [],
    dismissedTips: [],
    onboardingComplete: false,
  });
  const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    const fetchOnboardingState = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      // SessionStorage guard: if already dismissed this session, don't show again
      const sessionKey = `welcome_seen_${user.uid}`;
      const hasSeenThisSession = sessionStorage.getItem(sessionKey);

      if (hasSeenThisSession === 'true') {
        setShowWelcomeModal(false);
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const savedOnboarding = userData.onboarding || {};

          setOnboardingState(prev => ({
            ...prev,
            ...savedOnboarding
          }));

          // Check has_signed_in_first_time field - if true, show welcome modal
          // (true means this is their first sign-in, they haven't seen the modal yet)
          const isFirstSignIn = userData.has_signed_in_first_time === true;
          const hasNotSeenWelcome = !savedOnboarding.hasSeenWelcome || savedOnboarding.hasSeenWelcome === false;

          if (isFirstSignIn && hasNotSeenWelcome) {
            setShowWelcomeModal(true);
          } else {
            // Mark as seen in session to prevent re-showing on navigation
            sessionStorage.setItem(sessionKey, 'true');
          }
        } else {
          setShowWelcomeModal(true);
        }
      } catch (error) {
        console.error('Error fetching onboarding state:', error);
        // Don't show modal on error to avoid annoying users
        setShowWelcomeModal(false);
      } finally {
        setLoading(false);
      }
    };

    fetchOnboardingState();
  }, [user?.uid]);

  const updateOnboardingState = async (updates) => {
    if (!user?.uid) return;

    const newState = { ...onboardingState, ...updates };
    setOnboardingState(newState);

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        onboarding: newState
      });
    } catch (error) {
      console.error('Error updating onboarding state:', error);
    }
  };

  const markWelcomeSeen = async () => {
    setShowWelcomeModal(false);

    // Mark in sessionStorage to prevent re-showing on navigation
    if (user?.uid) {
      sessionStorage.setItem(`welcome_seen_${user.uid}`, 'true');

      // Update Firestore: set has_signed_in_first_time to false and hasSeenWelcome to true
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          has_signed_in_first_time: false,
        });
      } catch (error) {
        console.error('Error updating has_signed_in_first_time:', error);
      }
    }

    await updateOnboardingState({ hasSeenWelcome: true });
  };

  const markTipSeen = async (tipId) => {
    const dismissedTips = [...(onboardingState.dismissedTips || []), tipId];
    await updateOnboardingState({ dismissedTips });
  };

  const markStepComplete = async (stepId) => {
    const completedSteps = [...(onboardingState.completedSteps || []), stepId];
    await updateOnboardingState({ completedSteps });
  };

  const isTipDismissed = (tipId) => {
    return onboardingState.dismissedTips?.includes(tipId) || false;
  };

  const isStepComplete = (stepId) => {
    return onboardingState.completedSteps?.includes(stepId) || false;
  };

  const resetOnboarding = async () => {
    const freshState = {
      hasSeenWelcome: false,
      hasSeenDashboardTips: false,
      hasSeenEventsTips: false,
      hasSeenSettingsTips: false,
      hasSeenMarketplaceTips: false,
      hasSeenReceiptsTips: false,
      hasSeenCRMTips: false,
      hasSeenAnalyticsTips: false,
      completedSteps: [],
      dismissedTips: [],
      onboardingComplete: false,
    };
    setOnboardingState(freshState);
    setShowWelcomeModal(true);

    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { onboarding: freshState });
      } catch (error) {
        console.error('Error resetting onboarding:', error);
      }
    }
  };

  const value = {
    onboardingState,
    loading,
    showWelcomeModal,
    setShowWelcomeModal,
    markWelcomeSeen,
    markTipSeen,
    markStepComplete,
    isTipDismissed,
    isStepComplete,
    updateOnboardingState,
    resetOnboarding,
    userType,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
